import { Bestillingssammendrag } from "codegen";
import { format } from "date-fns";
import { debounce } from "lodash";
import { useMemo, useState } from "react";
import { Link } from "react-router-dom";

import { faFilterList } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Direction, Flex, Justify } from "@ntemarked/mittne-components/dist/Flex/Flex";

import { MainLayoutPageHeader } from "components/organisms/MainLayout";
import FormButton from "components/organisms/form/FormButton";
import { ToggleFieldClean } from "components/organisms/form/ToggleField";
import { useQuery } from "hooks/useParamsState";
import useQueryParamsState from "hooks/useQueryParamsState";
import { useStrombestillingApiQuery } from "hooks/useStrombestillingApi";
import { DatePicker } from "pages/Strombestilling/orders/components/DatePicker";
import { OrderTable } from "pages/Strombestilling/orders/components/OrderTable";
import OrdersFilters from "pages/Strombestilling/orders/components/OrdersFilters";

export function OrdersPage() {
	const today = new Date();
	const [selectedDate, setSelectedDate] = useState<Date>(today); // Kun for datepicker, ikke queryparam
	const [month, setMonth] = useQueryParamsState("month");
	const [isFullYear, setIsFullYear] = useQueryParamsState("year");
	const [status, setStatus] = useQueryParamsState("status");
	const [source, setSource] = useQueryParamsState("source");
	const [product, setProduct] = useQueryParamsState("product");
	const [customerName, setCustomerName] = useQueryParamsState("name");
	const [pending, setPending] = useQueryParamsState("pending");
	// For å kunne resette søkestrenger når man fjerner filter må søkefeltet ha en egen state, i tillegg til queryparam staten
	// Man kan ikke bruke samme ettersom debouncen legger inn et delay i oppdateringen
	const [searchName, setSearchName] = useState(customerName ?? "");

	function resetFilters() {
		setSelectedDate(today);
		setMonth("");
		setIsFullYear("");
		setStatus("");
		setSource("");
		setCustomerName("");
		setSearchName("");
		setProduct("");
		setShowFilters(false);
	}

	const anyFilterActivated = Boolean(month ?? isFullYear ?? status ?? source ?? customerName ?? product ?? pending);

	const [showFilters, setShowFilters] = useState(false);

	const params = useQuery();
	const { data, isLoading, isError } = useStrombestillingApiQuery<Bestillingssammendrag>(`/maintenance/ordreoversikt?${params.toString()}`, [
		"orders",
		params.toString(),
	]);

	const debouncedSetCustomerName = useMemo(() => debounce((newSearch: string) => setCustomerName(newSearch), 200), [setCustomerName]);

	return (
		<Flex style={{ maxWidth: "2000px" }} gap="1rem" direction={Direction.column}>
			<MainLayoutPageHeader title="Alle bestillinger" />
			<Flex direction={Direction.row} justifyContent={Justify.spaceBetween}>
				<Flex direction={Direction.row} gap="20px" alignItems={Justify.center}>
					<DatePicker
						date={selectedDate}
						setDate={(newDate) => {
							setSelectedDate(newDate);
							setMonth(format(newDate, "yyyy-MM"));
						}}
						isFullYear={!!isFullYear}
					/>
					<ToggleFieldClean
						checked={!!isFullYear}
						onChange={() => {
							if (isFullYear) {
								setIsFullYear("");
								setShowFilters(false);
							} else {
								setIsFullYear("true");
								setShowFilters(true);
							}
						}}
						label={`Vis statistikk for ${format(selectedDate, "yyyy")}`}
						wrapperStyle={{ width: "auto" }}
						labelStyle={{ color: "#28292c", fontWeight: 500 }}
					/>
				</Flex>
				<Flex direction={Direction.row} gap="20px" alignItems={Justify.center}>
					{anyFilterActivated && (
						<Link to="" onClick={resetFilters}>
							Nullstill filter
						</Link>
					)}
					<FormButton
						style={{ width: "180px" }}
						label={`${showFilters ? "Skjul" : "Åpne"} filtre`}
						icon={<FontAwesomeIcon style={{ marginLeft: "0.5rem" }} icon={faFilterList} />}
						onClick={() => setShowFilters(!showFilters)}
					/>
				</Flex>
			</Flex>
			{showFilters && data && (
				<OrdersFilters
					data={data}
					status={status}
					setStatus={setStatus}
					source={source}
					setSource={setSource}
					product={product}
					setProduct={setProduct}
					pending={pending}
					setPending={setPending}
				/>
			)}

			<Flex direction={Direction.row} gap="20px" alignItems={Justify.center} justifyContent={Justify.spaceBetween}>
				{data && (
					<p>
						Viser {data.filtrertAntall} av {data.totaltAntall} bestillinger
					</p>
				)}
				<input
					style={{ width: "800px" }}
					type="text"
					value={searchName}
					onChange={(e) => {
						setSearchName(e.target.value || "");
						debouncedSetCustomerName(e.target.value || "");
					}}
					placeholder="Søk etter kundenavn"
				/>
			</Flex>
			<OrderTable loading={isLoading && !data} error={isError} bestillinger={data?.bestillinger ?? []} />
		</Flex>
	);
}
